import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthUser } from "@billing/shared";

// BFF cookie-session model: the browser holds NO tokens. The real session is an
// HttpOnly cookie the browser can't read (__Host-bill_sid), validated server-
// side on every request. This store only caches the (non-secret) user profile
// already rendered in the UI, plus the CSRF token the server issued so writes
// can echo it in a header. It is mirrored to sessionStorage (not localStorage)
// so a reload can render immediately; bootstrap() then revalidates the cookie.
type AuthState = {
  user: AuthUser | null;
  csrf: string | null;
  /** True once a valid cookie session has been confirmed by the server. */
  authed: boolean;
  setSession: (user: AuthUser, csrf: string) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      csrf: null,
      authed: false,
      setSession: (user, csrf) => set({ user, csrf, authed: true }),
      clear: () => set({ user: null, csrf: null, authed: false }),
    }),
    {
      name: "dyuthi-auth",
      storage: createJSONStorage(() => sessionStorage),
      // Never persist the `authed` flag — it must be re-proven against the
      // cookie on every load via bootstrap().
      partialize: (s) => ({ user: s.user, csrf: s.csrf }),
    },
  ),
);

/** The CSRF token to echo on state-changing requests, if any. */
export function getCsrf(): string | null {
  return useAuthStore.getState().csrf;
}
